import React, { useState, useEffect } from "react";
import moment from "moment";

import { makeStyles } from "@material-ui/core/styles";
import { Avatar, Chip, Typography, Card, CardContent, CardActions, Button, IconButton, Menu, MenuItem } from "@material-ui/core";
import AccessTimeOutlinedIcon from "@material-ui/icons/AccessTimeOutlined";
import MapIcon from "@material-ui/icons/Map";
import AddIcon from "@material-ui/icons/Add";
import PrintIcon from "@material-ui/icons/Print";

import { DateTimeView } from "./DateTimeView";
import { RoomChip } from "./RoomChip";
import { ProfileChip } from "./ProfileChip";
import localization from "../../../utils/localizations";
import { Document, Room, Schedule } from "../../../types";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "inline-block",
  },
  title: {
    fontSize: 14,
    marginTop: 12,
  },
  onlineMeeting: {
    marginTop: 12,
    whiteSpace: "pre",
  },
  day: {
    fontSize: 14,
  },
  date: {
    fontSize: "60px",
    lineHeight: "60px",
  },
  month: {
    fontSize: 14,
  },
  itemMargin: {
    marginRight: 8,
    marginBottom: 8,
  },
  attendeesContainer: {
    maxWidth: 250,
  },
}));

function generateGoogleCalendarLink(event: Schedule) {
  const presentationType = (localization as any)[`presentation_${event.presentation_type}`]
    ? (localization as any)[`presentation_${event.presentation_type}`]
    : (localization as any)[`presentation`];
  return `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURI(presentationType)}&dates=${moment(event.start)
    .toISOString()
    .replace(/-|:|\.\d\d\d/g, "")}/${moment(event.end)
    .toISOString()
    .replace(/-|:|\.\d\d\d/g, "")}&details=${window.location.href}&location=${event.room ? encodeURI(event.room.name) : ""}&sf=true&output=xml`;
}

function generateICalLink(event: Schedule) {
  const presentationType = (localization as any)[`presentation_${event.presentation_type}`]
    ? (localization as any)[`presentation_${event.presentation_type}`]
    : (localization as any)[`presentation`];
  let ical = `BEGIN:VCALENDAR
VERSION:2.0
BEGIN:VEVENT
DTSTAMP:19960704T120000Z
UID:${event.id}
DTSTART:${moment(event.start)
    .toISOString()
    .replace(/-|:|\.\d\d\d/g, "")}
DTEND:${moment(event.end)
    .toISOString()
    .replace(/-|:|\.\d\d\d/g, "")}
SUMMARY:${presentationType}
DESCRIPTION:${window.location.href}
LOCATION:${event.room ? event.room.name : ""}
END:VEVENT
END:VCALENDAR`;

  return `data:application/octet-stream;base64,${btoa(ical)}`;
}

function EventItem(props: { event: Schedule; onPrint?: (eventId: string) => void; className?: string }) {
  const classes = useStyles();

  const [eventMenuAnchorEl, setEventMenuAnchorEl] = useState(null);

  const presentationType = (localization as any)[`presentation_${props.event.presentation_type}`]
    ? (localization as any)[`presentation_${props.event.presentation_type}`]
    : (localization as any)[`presentation`];

  return (
    <Card className={`${classes.root} ${props.className}`} variant="elevation">
      <CardContent>
        <Typography className={classes.day} color="textSecondary">
          {moment(props.event.start).format("dddd")}
        </Typography>
        <Typography className={classes.date}>{moment(props.event.start).format("DD")}</Typography>
        <Typography className={classes.month} color="textSecondary" gutterBottom>
          {moment(props.event.start).format("MMMM, yyyy")}
        </Typography>
        <div>
          <DateTimeView format=" " icon={<AccessTimeOutlinedIcon />} value={props.event.start} endValue={props.event.end} chip size="small" />
        </div>

        <Typography className={classes.title} color="textSecondary" gutterBottom>
          {presentationType}
        </Typography>
        {props.event.meeting_location && (
          <Typography className={classes.onlineMeeting} variant="body2" component="p">
            {props.event.meeting_location}
          </Typography>
        )}
        {props.event.online_meeting && (
          <Typography className={classes.onlineMeeting} variant="body2" component="p">
            {props.event.online_meeting}
          </Typography>
        )}
        {props.event.attendees && props.event.attendees.length > 0 && (
          <div>
            <Typography className={classes.title} color="textSecondary" gutterBottom>
              {localization.attendees}
            </Typography>
            <div className={classes.attendeesContainer}>
              {props.event.attendees.map((attendee, j) => {
                return <ProfileChip key={`userchip-${j}-${attendee.id}`} id={attendee.id} role="lecturers" size="small" className={classes.itemMargin} />;
              })}
            </div>
          </div>
        )}
      </CardContent>
      <CardActions>
        {props.event.room && <RoomChip room={props.event.room} />}
        <IconButton size="small" onClick={(event: any) => setEventMenuAnchorEl(event.currentTarget)}>
          <AddIcon />
        </IconButton>
        {!!props.onPrint && (
          <IconButton
            size="small"
            onClick={(event: any) => {
              if (props.onPrint) props.onPrint(props.event.id);
            }}
          >
            <PrintIcon />
          </IconButton>
        )}
        <Menu
          id={`event-save-menu-${props.event.id}`}
          anchorEl={eventMenuAnchorEl}
          keepMounted
          open={Boolean(eventMenuAnchorEl)}
          onClose={() => setEventMenuAnchorEl(null)}
        >
          <MenuItem
            onClick={() => {
              setEventMenuAnchorEl(null);
              window.open(generateGoogleCalendarLink(props.event), "_blank");
            }}
          >
            Google Calendar
          </MenuItem>
          <MenuItem
            onClick={() => {
              setEventMenuAnchorEl(null);
              const uri = generateICalLink(props.event);
              const filename = "event.ics";
              const link = document.createElement("a");

              if (typeof link.download === "string") {
                link.href = uri;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              } else {
                window.open(uri, "_blank");
              }
            }}
          >
            iCal (Outlook, iOS/MacOS Calendar)
          </MenuItem>
        </Menu>
      </CardActions>
    </Card>
  );
}

export { EventItem };
